// Runtime stylesheet writer facade using patch/replace/text strategies.
// Example: writeCssToStyleTag(styleTag, css).
import {
  readCachedCssFromStyleTag,
  readCachedSplitRules,
  readCssWriteModeFromStyleTag,
  readSheetRulesFromStyleTag,
  resolvePreviousCss,
  setCachedCssSnapshot,
} from './style-sheet/cache'
import { applyCssRulePatch, canPatchCssRules, canUseReplaceSync } from './style-sheet/patch'
import { splitTopLevelCssRules } from './style-sheet/split-rules'

export { readCachedCssFromStyleTag, readCssWriteModeFromStyleTag, readSheetRulesFromStyleTag }

// Optional write controls used by runtime debug/inspect features.
export type LuisStyleWriteOptions = {
  forceTextContent?: boolean
  preferCssom?: boolean
}

// Write CSS into a style tag while preserving authored text unless CSSOM mode is requested.
export function writeCssToStyleTag(styleTag: HTMLStyleElement, css: string, options: LuisStyleWriteOptions = {}): void {
  const previousCss = resolvePreviousCss(styleTag)
  // Skip DOM updates when target CSS is identical to previously applied CSS.
  if (previousCss === css) {
    setCachedCssSnapshot(styleTag, css, 'unchanged')
    return
  } else {
    // Continue with patch/replace fallback chain.
  }

  // Force plain text mode when inspect-friendly source mapping is requested.
  if (options.forceTextContent || !options.preferCssom) {
    styleTag.textContent = css
    setCachedCssSnapshot(styleTag, css, 'textContent')
    return
  } else {
    // Continue with patch/replace strategy for CSSOM-optimized mode.
  }

  const sheet = styleTag.sheet as CSSStyleSheet | null
  // Attempt incremental patch using top-level rule diff when API is available.
  if (canPatchCssRules(sheet)) {
    // Reuse cached split rules for previous CSS to avoid re-parsing.
    const previousRules = readCachedSplitRules(styleTag) ?? splitTopLevelCssRules(previousCss)
    const nextRules = splitTopLevelCssRules(css)
    if (applyCssRulePatch(sheet, previousRules, nextRules)) {
      setCachedCssSnapshot(styleTag, css, 'patch', nextRules)
      return
    } else {
      // Continue to replaceSync/text fallback when patch fails.
    }
  } else {
    // Continue to replaceSync/text fallback when patch API is unavailable.
  }

  // Use CSSStyleSheet API when browser exposes synchronous replacement.
  if (canUseReplaceSync(sheet)) {
    try {
      sheet.replaceSync(css)
      setCachedCssSnapshot(styleTag, css, 'replaceSync')
      return
      // Fall back to textContent when replaceSync fails for this sheet type.
    } catch {
      styleTag.textContent = css
      setCachedCssSnapshot(styleTag, css, 'textContent')
      return
    }
  } else {
    // Continue to textContent fallback when replaceSync is unavailable.
  }

  styleTag.textContent = css
  setCachedCssSnapshot(styleTag, css, 'textContent')
}
